export type TurnstileOptions = {
  sitekey: string;
  action: string;
  theme: 'light' | 'dark';
  language: string;
  size: 'flexible' | 'compact';
  'response-field': false;
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
  'timeout-callback': () => void;
};

export type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | undefined;

export const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    const fail = () => {
      window.clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      script.remove();
      scriptPromise = undefined;
      reject(new Error('Unable to load Turnstile'));
    };
    const timeout = window.setTimeout(fail, 15000);

    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => {
      if (!window.turnstile) {
        fail();
        return;
      }

      window.clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      resolve(window.turnstile);
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return scriptPromise;
};

export const getTurnstileHeaders = (token?: string): Record<string, string> | undefined =>
  token ? { 'x-captcha-token': token } : undefined;

// App locales supported by Turnstile. Bengali and Albanian fall back to English.
const TURNSTILE_LANGUAGES = new Set(['en', 'fr', 'de', 'el', 'sr']);

export const getTurnstileLanguage = (locale?: string): string => {
  const language = locale?.toLowerCase().split(/[-_]/)[0];

  return language && TURNSTILE_LANGUAGES.has(language) ? language : 'en';
};
